/**
 * Club settings screen (/groups/:id/settings) — admin-only, two tabs:
 * Settings (default format, digest cadence, beacon reminder, facility email,
 * who can poll) and Members (remove / promote / demote).
 */
import { useEffect, useState } from "react";

import {
  cancelRecurringSeries,
  createRecurringSessions,
  getGroupDetail,
  getGroupLadder,
  getRecurringSeries,
  removeMember,
  sendBeaconReminderNow,
  setBeaconReminder,
  setMemberRole,
  updateGroupSettings,
  updateRecurringSeries,
  type GroupDetailDTO,
  type GroupMemberDTO,
  type Me,
  type SeriesDTO,
} from "../api";
import { SeedLadderModal } from "../components/SeedLadderModal";

// A "mixed" club has no single default format; singles/doubles toggles (series,
// seed, ladder) fall back to doubles for their starting value.
function toggleDefault(f: "singles" | "doubles" | "mixed"): "singles" | "doubles" {
  return f === "mixed" ? "doubles" : f;
}

const WD_LABEL = ["M", "T", "W", "T", "F", "S", "S"]; // Mon=0..Sun=6
const WD_NAME = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export function ClubSettingsScreen({
  groupId,
  me,
  onBack,
}: {
  groupId: string;
  me: Me;
  onBack: () => void;
}) {
  const [detail, setDetail] = useState<GroupDetailDTO | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<"settings" | "weekly" | "members">("settings");

  async function reload() {
    try {
      setDetail(await getGroupDetail(groupId));
    } catch (e) {
      setError(String((e as Error).message));
    }
  }
  useEffect(() => { void reload(); /* eslint-disable-next-line */ }, [groupId]);

  if (error) {
    return <div className="max-w-md mx-auto px-5 pt-10 text-[#C84B31]">{error}</div>;
  }
  if (!detail) {
    return <div className="max-w-md mx-auto px-5 pt-10 text-ink/55">Loading…</div>;
  }
  if (detail.role !== "admin") {
    return (
      <div className="max-w-md mx-auto px-5 pt-10">
        <button onClick={onBack} className="font-mono text-[11px] uppercase tracking-wider text-ink/55 hover:text-ink mb-6">← Back</button>
        <p className="text-ink/60">Only admins can manage settings.</p>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto px-5 pt-8">
      <button onClick={onBack} className="font-mono text-[11px] uppercase tracking-wider text-ink/55 hover:text-ink mb-4">
        ← {detail.name}
      </button>
      <h1 className="font-display font-bold text-3xl tracking-tight mb-5">Settings</h1>

      <div className="flex gap-1 mb-6 border-b border-ink/10">
        {(["settings", "weekly", "members"] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={
              "px-4 py-2 font-mono text-[11px] uppercase tracking-wider -mb-px border-b-2 capitalize " +
              (tab === t ? "border-ink text-ink" : "border-transparent text-ink/45 hover:text-ink/70")
            }
          >
            {t === "members" ? `Members · ${detail.member_count}` : t === "weekly" ? "Weekly" : "Settings"}
          </button>
        ))}
      </div>

      {tab === "settings" ? (
        <SettingsTab detail={detail} onSaved={reload} />
      ) : tab === "weekly" ? (
        <WeeklyTab detail={detail} />
      ) : (
        <MembersTab detail={detail} meId={me.uid} onChanged={reload} />
      )}
    </div>
  );
}

function WeekdayPicker({
  value,
  onChange,
}: {
  value: number[];
  onChange: (days: number[]) => void;
}) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {WD_LABEL.map((label, d) => (
        <button
          key={d}
          title={WD_NAME[d]}
          onClick={() => onChange(value.includes(d) ? value.filter((x) => x !== d) : [...value, d])}
          className={
            "w-9 h-9 rounded-full font-display font-bold text-sm cs-tap " +
            (value.includes(d) ? "bg-accent text-ink" : "border border-ink/20 text-ink/50")
          }
        >
          {label}
        </button>
      ))}
    </div>
  );
}

function SettingsTab({ detail, onSaved }: { detail: GroupDetailDTO; onSaved: () => void }) {
  const padelLocked = detail.sport === "padel";
  const [format, setFormat] = useState<"singles" | "doubles" | "mixed">(detail.default_format);
  const [digest, setDigest] = useState<number[]>(
    (detail.settings?.digestDays as number[] | undefined) ?? [6],
  );
  const [beacon, setBeacon] = useState<number[]>(detail.beacon_reminder_days);
  const [facility, setFacility] = useState(detail.facility_email ?? "");
  const [lockHours, setLockHours] = useState(String(detail.lock_hours ?? 8));
  const [pollsAdminOnly, setPollsAdminOnly] = useState(
    (detail.settings?.pollsAdminOnly ?? true) as boolean,
  );
  const [showLadder, setShowLadder] = useState(
    (detail.settings?.visibilityStandings ?? "hidden") === "public",
  );
  const [showResults, setShowResults] = useState(
    (detail.settings?.sessionResults ?? true) as boolean,
  );
  const [saving, setSaving] = useState(false);
  const [sentMsg, setSentMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // Hand-seed the ladder (rare, so it lives here rather than the ladder header).
  const [seedOrder, setSeedOrder] = useState<GroupMemberDTO[] | null>(null);
  const [seedBusy, setSeedBusy] = useState(false);
  const [seedMsg, setSeedMsg] = useState<string | null>(null);

  async function openSeed() {
    setSeedBusy(true);
    setErr(null);
    try {
      // Pre-fill the seed order from the current ladder standings (ranked
      // players first), then anyone not yet on the ladder in roster order.
      const ladder = await getGroupLadder(detail.id, toggleDefault(detail.default_format));
      const rank = new Map(ladder.entries.map((e) => [e.user_id, e.position]));
      setSeedOrder(
        [...detail.members].sort(
          (a, b) =>
            (rank.get(a.user_id) ?? Infinity) - (rank.get(b.user_id) ?? Infinity),
        ),
      );
    } catch (e) {
      setErr(String((e as Error).message));
    } finally {
      setSeedBusy(false);
    }
  }

  async function save() {
    const hours = Number(lockHours);
    if (!Number.isInteger(hours) || hours < 1 || hours > 168) {
      setErr("Roster lock must be a whole number of hours between 1 and 168.");
      return;
    }
    setSaving(true);
    setErr(null);
    try {
      await updateGroupSettings(detail.id, {
        default_format: padelLocked ? "doubles" : format,
        digest_days: digest,
        facility_email: facility.trim(),
        polls_admin_only: pollsAdminOnly,
        visibility_standings: showLadder ? "public" : "hidden",
        session_results: showResults,
        lock_hours: hours,
      });
      await setBeaconReminder(detail.id, beacon);
      onSaved();
    } catch (e) {
      setErr(String((e as Error).message));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-7 pb-12">
      <Field
        label="Default format"
        hint={
          padelLocked
            ? "Padel is doubles-only."
            : "Mixed = a club that plays both; pick the format per game."
        }
      >
        <div className="flex gap-2">
          {(["singles", "doubles", "mixed"] as const).map((f) => (
            <button
              key={f}
              disabled={padelLocked && f !== "doubles"}
              onClick={() => setFormat(f)}
              className={
                "flex-1 px-3 py-2.5 rounded-md font-display font-bold text-sm cs-tap border-2 capitalize disabled:opacity-30 " +
                ((padelLocked ? "doubles" : format) === f ? "bg-accent text-ink border-ink" : "border-ink/15")
              }
            >
              {f}
            </button>
          ))}
        </div>
      </Field>

      <Field label="Digest email" hint="Which weekdays the upcoming-sessions email goes out. None = off.">
        <WeekdayPicker value={digest} onChange={setDigest} />
      </Field>

      <Field label="Beacon reminder" hint="Weekly nudge to post a pickup game. None = off.">
        <WeekdayPicker value={beacon} onChange={setBeacon} />
        <button
          onClick={async () => {
            try {
              const r = await sendBeaconReminderNow(detail.id);
              setSentMsg(`Sent to ${r.sent} member${r.sent === 1 ? "" : "s"}.`);
            } catch (e) { setErr(String((e as Error).message)); }
          }}
          className="mt-3 font-mono text-[10px] uppercase tracking-wider border border-ink/20 hover:border-ink/40 px-3 py-1.5 cs-tap"
        >
          Send one now
        </button>
        {sentMsg && <span className="text-sm text-success ml-3">{sentMsg}</span>}
      </Field>

      <Field label="Facility email" hint="Gets the auto reservation request when a session hits min attendance.">
        <input
          value={facility}
          onChange={(e) => setFacility(e.target.value)}
          placeholder="bookings@yourclub.com"
          className="w-full rounded-md border border-ink/20 px-3 py-2.5 text-base focus:outline-none focus:border-ink/50 bg-bg"
        />
      </Field>

      <Field
        label="Roster lock"
        hint="Hours before a session when the roster locks: courts are assigned from whoever's confirmed, the facility gets the court request, and later yeses join the waitlist."
      >
        <div className="flex items-center gap-2">
          <input
            type="number"
            min={1}
            max={168}
            value={lockHours}
            onChange={(e) => setLockHours(e.target.value)}
            className="w-24 rounded-md border border-ink/20 px-3 py-2.5 text-base focus:outline-none focus:border-ink/50 bg-bg"
          />
          <span className="text-sm text-ink/60">hours before start</span>
        </div>
      </Field>

      <Field label="Who can create polls">
        <div className="flex gap-2">
          {[
            { v: true, label: "Admins only" },
            { v: false, label: "Anyone" },
          ].map((o) => (
            <button
              key={String(o.v)}
              onClick={() => setPollsAdminOnly(o.v)}
              className={
                "flex-1 px-3 py-2.5 rounded-md font-display font-bold text-sm cs-tap border-2 " +
                (pollsAdminOnly === o.v ? "bg-ink text-bg border-ink" : "border-ink/15")
              }
            >
              {o.label}
            </button>
          ))}
        </div>
      </Field>

      <Field
        label="Ladder & standings"
        hint="Show the ranked ladder to members. Off keeps it hidden — ratings still run quietly for balanced matchups."
      >
        <div className="flex gap-2">
          {[
            { v: true, label: "Show ladder" },
            { v: false, label: "Hidden" },
          ].map((o) => (
            <button
              key={String(o.v)}
              onClick={() => setShowLadder(o.v)}
              className={
                "flex-1 px-3 py-2.5 rounded-md font-display font-bold text-sm cs-tap border-2 " +
                (showLadder === o.v ? "bg-ink text-bg border-ink" : "border-ink/15")
              }
            >
              {o.label}
            </button>
          ))}
        </div>
        <div className="mt-3 flex items-center gap-3 flex-wrap">
          <button
            onClick={() => void openSeed()}
            disabled={seedBusy}
            className="font-mono text-[10px] uppercase tracking-wider border border-ink/20 hover:border-ink/40 px-3 py-1.5 cs-tap disabled:opacity-40"
          >
            {seedBusy ? "Loading…" : `Set ${toggleDefault(detail.default_format)} ladder order →`}
          </button>
          {seedMsg && <span className="text-sm text-success">{seedMsg}</span>}
        </div>
      </Field>

      <Field
        label="Session results"
        hint="Show recorded results on each session to its players. Off keeps them admin-only."
      >
        <div className="flex gap-2">
          {[
            { v: true, label: "Show results" },
            { v: false, label: "Admin only" },
          ].map((o) => (
            <button
              key={String(o.v)}
              onClick={() => setShowResults(o.v)}
              className={
                "flex-1 px-3 py-2.5 rounded-md font-display font-bold text-sm cs-tap border-2 " +
                (showResults === o.v ? "bg-ink text-bg border-ink" : "border-ink/15")
              }
            >
              {o.label}
            </button>
          ))}
        </div>
      </Field>

      {err && <div className="text-sm text-[#C84B31]">{err}</div>}
      <button
        onClick={() => void save()}
        disabled={saving}
        className="w-full px-5 py-3 bg-ink text-bg font-display font-bold text-base cs-tap rounded-md disabled:opacity-40"
      >
        {saving ? "Saving…" : "Save settings"}
      </button>

      {seedOrder && (
        <SeedLadderModal
          groupId={detail.id}
          members={seedOrder}
          format={toggleDefault(detail.default_format)}
          onClose={(seeded) => {
            setSeedOrder(null);
            if (seeded) setSeedMsg("Ladder order saved.");
          }}
        />
      )}
    </div>
  );
}

const WD_FULL = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

function fmtTime(hhmm: string): string {
  const [hStr, mStr] = hhmm.split(":");
  const h = Number(hStr ?? 0);
  const m = Number(mStr ?? 0);
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, "0")} ${ampm}`;
}

function WeeklyTab({ detail }: { detail: GroupDetailDTO }) {
  const [series, setSeries] = useState<SeriesDTO[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function reload() {
    try {
      setSeries(await getRecurringSeries(detail.id));
    } catch (e) {
      setErr(String((e as Error).message));
    }
  }
  useEffect(() => { void reload(); /* eslint-disable-next-line */ }, [detail.id]);

  return (
    <div className="space-y-8 pb-12">
      {err && <div className="text-sm text-[#C84B31]">{err}</div>}

      {series && series.length > 0 && (
        <div className="space-y-3">
          <div className="font-display font-bold text-lg">Your weekly games</div>
          {series.map((s) => (
            <SeriesCard key={s.id} detail={detail} series={s} onChanged={reload} />
          ))}
        </div>
      )}

      <div>
        <div className="font-display font-bold text-lg mb-1">
          {series && series.length > 0 ? "Add another weekly game" : "Set up a weekly game"}
        </div>
        <CreateSeriesForm detail={detail} onCreated={reload} />
      </div>
    </div>
  );
}

function SeriesCard({
  detail,
  series,
  onChanged,
}: {
  detail: GroupDetailDTO;
  series: SeriesDTO;
  onChanged: () => void;
}) {
  const padelLocked = detail.sport === "padel";
  const [editing, setEditing] = useState(false);
  const [location, setLocation] = useState(series.location ?? "");
  const [format, setFormat] = useState<"singles" | "doubles">(series.format ?? toggleDefault(detail.default_format));
  const [courts, setCourts] = useState(series.court_count ?? 1);
  const [min, setMin] = useState<number | "">(series.min_attendance ?? "");
  const [max, setMax] = useState<number | "">(series.capacity ?? "");
  const [courtNumbers, setCourtNumbers] = useState((series.court_labels ?? []).join(", "));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const effFormat = padelLocked ? "doubles" : format;

  async function save() {
    setBusy(true);
    setErr(null);
    try {
      await updateRecurringSeries(detail.id, series.id, {
        location: location.trim() || null,
        format: effFormat,
        court_count: courts || null,
        capacity: max === "" ? null : Number(max),
        min_attendance: min === "" ? null : Number(min),
        court_labels: courtNumbers.split(",").map((c) => c.trim()).filter(Boolean),
      });
      setEditing(false);
      onChanged();
    } catch (e) {
      setErr(String((e as Error).message));
    } finally {
      setBusy(false);
    }
  }

  async function cancel() {
    if (!window.confirm(
      `Cancel this series? Its ${series.upcoming_count} upcoming session` +
      `${series.upcoming_count === 1 ? "" : "s"} will be cancelled. Past games are kept.`,
    )) return;
    setBusy(true);
    setErr(null);
    try {
      await cancelRecurringSeries(detail.id, series.id);
      onChanged();
    } catch (e) {
      setErr(String((e as Error).message));
      setBusy(false);
    }
  }

  const metaBits = [
    effFormat === "singles" ? "Singles" : "Doubles",
    series.court_count ? `${series.court_count} court${series.court_count === 1 ? "" : "s"}` : null,
    series.min_attendance ? `min ${series.min_attendance}` : null,
    series.capacity ? `max ${series.capacity}` : null,
    series.court_labels?.length ? `courts ${series.court_labels.join(", ")}` : null,
    series.location || null,
  ].filter(Boolean);

  return (
    <div className="border border-ink/15 rounded-md px-4 py-3.5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-display font-bold text-base">
            {WD_FULL[series.weekday]}s · {fmtTime(series.time)}
          </div>
          <div className="text-sm text-ink/55">{metaBits.join(" · ")}</div>
          <div className="font-mono text-[10px] uppercase tracking-wider text-ink/45 mt-1">
            {series.upcoming_count} upcoming
          </div>
        </div>
        {!editing && (
          <div className="flex gap-1 shrink-0">
            <button
              onClick={() => setEditing(true)}
              className="font-mono text-[10px] uppercase tracking-wider border border-ink/20 hover:border-ink/40 px-2.5 py-1 cs-tap"
            >
              Edit
            </button>
            <button
              onClick={() => void cancel()}
              disabled={busy}
              className="font-mono text-[10px] uppercase tracking-wider border border-ink/20 hover:border-[#C84B31] hover:text-[#C84B31] px-2.5 py-1 cs-tap disabled:opacity-40"
            >
              Cancel
            </button>
          </div>
        )}
      </div>

      {editing && (
        <div className="mt-4 space-y-4 border-t border-ink/10 pt-4">
          <p className="text-xs text-ink/45">
            Changes apply to all upcoming sessions. To move the day, time, or
            length, cancel this series and create a new one.
          </p>
          <Field label="Location">
            <input
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="Riverside Courts"
              className="w-full rounded-md border border-ink/20 px-3 py-2.5 text-base focus:outline-none focus:border-ink/50 bg-bg"
            />
          </Field>
          <Field label="Format" hint={padelLocked ? "Padel is doubles-only." : undefined}>
            <div className="flex gap-2">
              {(["singles", "doubles"] as const).map((f) => (
                <button
                  key={f}
                  disabled={padelLocked && f === "singles"}
                  onClick={() => setFormat(f)}
                  className={
                    "flex-1 px-3 py-2.5 rounded-md font-display font-bold text-sm cs-tap border-2 capitalize disabled:opacity-30 " +
                    (effFormat === f ? "bg-accent text-ink border-ink" : "border-ink/15")
                  }
                >
                  {f}
                </button>
              ))}
            </div>
          </Field>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Courts">
              <input
                type="number" min={1} value={courts}
                onChange={(e) => setCourts(Number(e.target.value))}
                className="w-full rounded-md border border-ink/20 px-3 py-2.5 text-base focus:outline-none focus:border-ink/50 bg-bg"
              />
            </Field>
            <Field label="Min">
              <input
                type="number" min={1} value={min} placeholder="—"
                onChange={(e) => setMin(e.target.value === "" ? "" : Number(e.target.value))}
                className="w-full rounded-md border border-ink/20 px-3 py-2.5 text-base focus:outline-none focus:border-ink/50 bg-bg"
              />
            </Field>
            <Field label="Max">
              <input
                type="number" min={1} value={max} placeholder="—"
                onChange={(e) => setMax(e.target.value === "" ? "" : Number(e.target.value))}
                className="w-full rounded-md border border-ink/20 px-3 py-2.5 text-base focus:outline-none focus:border-ink/50 bg-bg"
              />
            </Field>
          </div>
          <Field label="Court numbers" hint="Usual courts, in order — e.g. 5, 6, 7. Extras are used only if more courts fill.">
            <input
              value={courtNumbers}
              onChange={(e) => setCourtNumbers(e.target.value)}
              placeholder="5, 6, 7"
              className="w-full rounded-md border border-ink/20 px-3 py-2.5 text-base focus:outline-none focus:border-ink/50 bg-bg"
            />
          </Field>
          {err && <div className="text-sm text-[#C84B31]">{err}</div>}
          <div className="flex gap-2">
            <button
              onClick={() => void save()}
              disabled={busy}
              className="px-4 py-2 bg-ink text-bg font-display font-bold text-sm cs-tap rounded-md disabled:opacity-40"
            >
              {busy ? "Saving…" : "Save changes"}
            </button>
            <button
              onClick={() => setEditing(false)}
              className="px-4 py-2 border border-ink/20 font-display font-bold text-sm cs-tap rounded-md"
            >
              Discard
            </button>
          </div>
        </div>
      )}
      {!editing && err && <div className="text-sm text-[#C84B31] mt-2">{err}</div>}
    </div>
  );
}

function CreateSeriesForm({ detail, onCreated }: { detail: GroupDetailDTO; onCreated: () => void }) {
  const padelLocked = detail.sport === "padel";
  const [weekday, setWeekday] = useState(1); // Tue default
  const [time, setTime] = useState("18:00");
  const [duration, setDuration] = useState(90);
  const [location, setLocation] = useState("");
  const [format, setFormat] = useState<"singles" | "doubles">(toggleDefault(detail.default_format));
  const [courts, setCourts] = useState(2);
  const [maxTouched, setMaxTouched] = useState(false);
  const [max, setMax] = useState<number | "">("");
  const [min, setMin] = useState<number | "">("");
  const [weeks, setWeeks] = useState(12);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const effFormat = padelLocked ? "doubles" : format;
  const perCourt = effFormat === "singles" ? 2 : 4;
  const autoMax = courts > 0 ? perCourt * courts : 0;
  const shownMax = maxTouched ? max : autoMax || "";

  async function submit() {
    setSaving(true);
    setErr(null);
    setDone(null);
    try {
      const maxVal = maxTouched ? (max === "" ? null : Number(max)) : autoMax || null;
      const r = await createRecurringSessions(detail.id, {
        weekday,
        time,
        duration_minutes: duration,
        weeks,
        location: location.trim() || null,
        format: effFormat,
        court_count: courts || null,
        capacity: maxVal,
        min_attendance: min === "" ? null : Number(min),
      });
      const when = r.first_starts_at
        ? new Date(r.first_starts_at).toLocaleDateString(undefined, {
            weekday: "long", month: "short", day: "numeric",
          })
        : "";
      setDone(`Created ${r.created} weekly session${r.created === 1 ? "" : "s"}, starting ${when}.`);
      onCreated();
    } catch (e) {
      setErr(String((e as Error).message));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-7 pb-12">
      <p className="text-sm text-ink/55 -mt-1">
        Set a standing weekly game. We'll create the next {weeks} on the schedule
        and everyone can RSVP.
      </p>

      <Field label="Day">
        <div className="flex flex-wrap gap-1.5">
          {WD_LABEL.map((label, d) => (
            <button
              key={d}
              title={WD_NAME[d]}
              onClick={() => setWeekday(d)}
              className={
                "w-9 h-9 rounded-full font-display font-bold text-sm cs-tap " +
                (weekday === d ? "bg-accent text-ink" : "border border-ink/20 text-ink/50")
              }
            >
              {label}
            </button>
          ))}
        </div>
      </Field>

      <div className="grid grid-cols-2 gap-4">
        <Field label="Start time">
          <input
            type="time"
            value={time}
            onChange={(e) => setTime(e.target.value)}
            className="w-full rounded-md border border-ink/20 px-3 py-2.5 text-base focus:outline-none focus:border-ink/50 bg-bg"
          />
        </Field>
        <Field label="Length (min)">
          <input
            type="number"
            min={15}
            step={15}
            value={duration}
            onChange={(e) => setDuration(Number(e.target.value))}
            className="w-full rounded-md border border-ink/20 px-3 py-2.5 text-base focus:outline-none focus:border-ink/50 bg-bg"
          />
        </Field>
      </div>

      <Field label="Location">
        <input
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          placeholder="Riverside Courts"
          className="w-full rounded-md border border-ink/20 px-3 py-2.5 text-base focus:outline-none focus:border-ink/50 bg-bg"
        />
      </Field>

      <Field label="Format" hint={padelLocked ? "Padel is doubles-only." : undefined}>
        <div className="flex gap-2">
          {(["singles", "doubles"] as const).map((f) => (
            <button
              key={f}
              disabled={padelLocked && f === "singles"}
              onClick={() => setFormat(f)}
              className={
                "flex-1 px-3 py-2.5 rounded-md font-display font-bold text-sm cs-tap border-2 capitalize disabled:opacity-30 " +
                (effFormat === f ? "bg-accent text-ink border-ink" : "border-ink/15")
              }
            >
              {f}
            </button>
          ))}
        </div>
      </Field>

      <div className="grid grid-cols-3 gap-3">
        <Field label="Courts">
          <input
            type="number"
            min={1}
            value={courts}
            onChange={(e) => setCourts(Number(e.target.value))}
            className="w-full rounded-md border border-ink/20 px-3 py-2.5 text-base focus:outline-none focus:border-ink/50 bg-bg"
          />
        </Field>
        <Field label="Min to play">
          <input
            type="number"
            min={1}
            value={min}
            onChange={(e) => setMin(e.target.value === "" ? "" : Number(e.target.value))}
            placeholder="—"
            className="w-full rounded-md border border-ink/20 px-3 py-2.5 text-base focus:outline-none focus:border-ink/50 bg-bg"
          />
        </Field>
        <Field label="Max players">
          <input
            type="number"
            min={1}
            value={shownMax}
            onChange={(e) => { setMaxTouched(true); setMax(e.target.value === "" ? "" : Number(e.target.value)); }}
            className="w-full rounded-md border border-ink/20 px-3 py-2.5 text-base focus:outline-none focus:border-ink/50 bg-bg"
          />
        </Field>
      </div>
      <p className="text-xs text-ink/45 -mt-4">
        Min triggers the facility reservation email + "game on" once reached.
        Max defaults to courts × {perCourt}; anyone past it goes on the waitlist.
      </p>

      <Field label="How many weeks">
        <input
          type="number"
          min={1}
          max={52}
          value={weeks}
          onChange={(e) => setWeeks(Number(e.target.value))}
          className="w-full rounded-md border border-ink/20 px-3 py-2.5 text-base focus:outline-none focus:border-ink/50 bg-bg"
        />
      </Field>

      {err && <div className="text-sm text-[#C84B31]">{err}</div>}
      {done && <div className="text-sm text-success">{done}</div>}
      <button
        onClick={() => void submit()}
        disabled={saving}
        className="w-full px-5 py-3 bg-ink text-bg font-display font-bold text-base cs-tap rounded-md disabled:opacity-40"
      >
        {saving ? "Creating…" : "Create weekly sessions"}
      </button>
    </div>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="font-display font-bold text-base mb-1">{label}</div>
      {hint && <div className="text-sm text-ink/55 mb-2.5">{hint}</div>}
      {children}
    </div>
  );
}

function MembersTab({
  detail,
  meId,
  onChanged,
}: {
  detail: GroupDetailDTO;
  meId: string;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const adminCount = detail.members.filter((m) => m.role === "admin").length;

  async function act(fn: () => Promise<void>, who: string) {
    setBusy(who);
    setErr(null);
    try {
      await fn();
      onChanged();
    } catch (e) {
      setErr(String((e as Error).message));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="space-y-2 pb-12">
      {err && <div className="text-sm text-[#C84B31] mb-2">{err}</div>}
      {detail.members.map((m: GroupMemberDTO) => {
        const isMe = m.user_id === meId;
        const lastAdmin = m.role === "admin" && adminCount <= 1;
        return (
          <div key={m.user_id} className="flex items-center gap-3 border border-ink/15 px-4 py-3 rounded-md">
            <div className="w-9 h-9 rounded-full bg-ink text-bg flex items-center justify-center font-display font-bold text-xs shrink-0">
              {m.initials}
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-display font-bold text-sm truncate">
                {m.name}{isMe && <span className="font-mono text-[9px] uppercase text-ink/45 ml-1">you</span>}
              </div>
              <div className="font-mono text-[10px] uppercase tracking-wider text-ink/50">
                {m.role}{m.joined_at ? ` · joined ${m.joined_at}` : ""}
              </div>
            </div>
            <div className="flex gap-1.5 shrink-0">
              {m.role === "member" ? (
                <button
                  disabled={busy === m.user_id}
                  onClick={() => act(() => setMemberRole(detail.id, m.user_id, "admin"), m.user_id)}
                  className="font-mono text-[10px] uppercase tracking-wider border border-ink/20 hover:border-ink/40 px-2.5 py-1 cs-tap"
                >
                  Make admin
                </button>
              ) : (
                <button
                  disabled={busy === m.user_id || lastAdmin}
                  onClick={() => act(() => setMemberRole(detail.id, m.user_id, "member"), m.user_id)}
                  className="font-mono text-[10px] uppercase tracking-wider border border-ink/20 hover:border-ink/40 px-2.5 py-1 cs-tap disabled:opacity-30"
                  title={lastAdmin ? "Can't demote the last admin" : undefined}
                >
                  Demote
                </button>
              )}
              {!isMe && (
                <button
                  disabled={busy === m.user_id || lastAdmin}
                  onClick={() => {
                    if (!window.confirm(`Remove ${m.name} from the club?`)) return;
                    act(() => removeMember(detail.id, m.user_id), m.user_id);
                  }}
                  className="font-mono text-[10px] uppercase tracking-wider border border-[#C84B31]/40 text-[#C84B31] hover:bg-[#C84B31]/5 px-2.5 py-1 cs-tap disabled:opacity-30"
                  title={lastAdmin ? "Can't remove the last admin" : undefined}
                >
                  Remove
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
